import {useCallback, useState} from 'react';

import {EditTrustAccountModal} from '@/features/trust-accounts/modals';
import {TrustAccountProvider} from '@/features/trust-accounts/providers';
import {PageContent} from '@/layouts';

import {QuickActionsFab} from './QuickActionsFab';
import {TrustAccountPageLayout} from './TrustAccountPageLayout';
import {TrustAccountViewHeader, type TrustAccountViewTab} from './TrustAccountViewHeader';
import {useTrustAccountBadgeCounts} from '../hooks';
import {ActivityTabContent} from './tabs/activity';
import {BeneficiariesTabContent} from './tabs/beneficiaries';
import {BuyersTabContent} from './tabs/buyers';
import {DocumentsTabContent} from './tabs/documents';
import {InstructionsTabContent} from './tabs/instructions';
import {PayoutsTabContent} from './tabs/payouts';
import {TransactionsTabContent} from './tabs/transactions';

import type {TrustStatus} from '@sollapay/enums';
import type {TrustAccountDTO, TrustAccountLedgerDTO, TrustSellerDetailsDTO} from '@sollapay/types';
import type {FC} from 'react';

export interface TrustAccountViewProps {
  id: string;
  name: string;
  refId: string;
  status: TrustStatus;
  balance?: TrustAccountLedgerDTO | null;
  balanceUpdatedAt?: number;
  isBalanceLoading?: boolean;
  sellers: TrustSellerDetailsDTO[];
  purpose?: string | null;
  transactionType?: string | null;
  account: TrustAccountDTO['trustAccount'];
  activeTab: TrustAccountViewTab;
  onTabChange: (tab: TrustAccountViewTab) => void;
  error?: unknown;
  refetch?: () => void;
}

const getTabContent = (activeTab: TrustAccountViewTab) => {
  switch (activeTab) {
    case 'overview':
      return <ActivityTabContent />;
    case 'buyers':
      return <BuyersTabContent />;
    case 'transactions':
      return <TransactionsTabContent />;
    case 'documents':
      return <DocumentsTabContent />;
    case 'beneficiaries':
      return <BeneficiariesTabContent />;
    case 'payouts':
      return <PayoutsTabContent />;
    case 'instructions':
      return <InstructionsTabContent />;
    default:
      return null;
  }
};

export const TrustAccountView: FC<TrustAccountViewProps> = ({
  id,
  name,
  refId,
  status,
  balance,
  balanceUpdatedAt,
  isBalanceLoading,
  sellers,
  purpose,
  transactionType,
  account,
  activeTab,
  onTabChange,
  error,
  refetch
}) => {
  const {
    buyersCount,
    beneficiariesCount,
    transactionsCount,
    documentsCount,
    payoutsCount,
    paymentInstructionsCount
  } = useTrustAccountBadgeCounts({trustAccountId: id});
  const [isEditOpen, setIsEditOpen] = useState(false);
  const handleEditAccount = useCallback(() => setIsEditOpen(true), []);
  const handleCloseEdit = useCallback(() => setIsEditOpen(false), []);

  return (
    <TrustAccountProvider account={account}>
      <TrustAccountPageLayout error={error} refetch={refetch}>
        <TrustAccountViewHeader
          trustAccountId={id}
          name={name}
          refId={refId}
          status={status}
          balance={balance}
          balanceUpdatedAt={balanceUpdatedAt}
          isBalanceLoading={isBalanceLoading}
          sellers={sellers}
          purpose={purpose}
          transactionType={transactionType}
          activeTab={activeTab}
          buyersCount={buyersCount}
          transactionsCount={transactionsCount}
          documentsCount={documentsCount}
          beneficiariesCount={beneficiariesCount}
          payoutsCount={payoutsCount}
          paymentInstructionsCount={paymentInstructionsCount}
          onTabChange={onTabChange}
          onEditAccount={handleEditAccount}
        />
        <PageContent>{getTabContent(activeTab)}</PageContent>
        <QuickActionsFab />
        <EditTrustAccountModal account={isEditOpen ? account : null} onClose={handleCloseEdit} />
      </TrustAccountPageLayout>
    </TrustAccountProvider>
  );
};
